type DiagnosticConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

type DiagnosticConsoleEntry = {
  ts: number
  level: DiagnosticConsoleLevel
  text: string
}

type DiagnosticConsoleMethods = Partial<
  Record<DiagnosticConsoleLevel, (...args: any[]) => void>
>

const FG_NATIVE_CONSOLE_KEY = '__FG_NATIVE_CONSOLE__'
const FG_CONSOLE_BUFFER_KEY = '__FG_CONSOLE_BUFFER__'
const FG_CONSOLE_EVENT = 'fg_console_entry'
const FG_CONSOLE_BUFFER_MAX = 600

const diagnosticConsoleLevels: DiagnosticConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug']

const getWindowAny = (): any | null => {
  if (typeof window === 'undefined') return null
  return window as any
}

const isTruthyFlag = (value: any): boolean =>
  ['1', 'true', 'yes', 'on'].includes(String(value ?? '').trim().toLowerCase())

const shouldMirrorDiagnosticConsoleLine = (level: DiagnosticConsoleLevel): boolean => {
  if (level === 'error') return true
  const w = getWindowAny()
  if (!w) return true
  if (w.__FG_DIAG === true || w.__FG_PERF === true || w.__FG_KEEP_PERF_LOGS__ === true) return true
  try {
    const sp = new URLSearchParams(String(window.location?.search || ''))
    if (isTruthyFlag(sp.get('fg_diag_console')) || isTruthyFlag(sp.get('fg_keep_perf_logs'))) return true
  } catch {
    // ignore URL parsing failures
  }
  try {
    if (
      isTruthyFlag(window.localStorage?.getItem('fg_diag_console')) ||
      isTruthyFlag(window.localStorage?.getItem('fg_keep_perf_logs'))
    ) {
      return true
    }
  } catch {
    // ignore storage access failures
  }
  return false
}

export function installDiagnosticConsoleCapture(): void {
  const w = getWindowAny()
  if (!w || w[FG_NATIVE_CONSOLE_KEY]) return
  const methods: DiagnosticConsoleMethods = {}
  for (const level of diagnosticConsoleLevels) {
    const method = console[level]
    if (typeof method === 'function') methods[level] = method.bind(console)
  }
  w[FG_NATIVE_CONSOLE_KEY] = methods
}

const appendDiagnosticConsoleBuffer = (level: DiagnosticConsoleLevel, text: string): void => {
  const w = getWindowAny()
  if (!w) return
  const buf = w[FG_CONSOLE_BUFFER_KEY]
  if (!Array.isArray(buf)) return
  buf.push({
    ts: Date.now(),
    level,
    text,
  } satisfies DiagnosticConsoleEntry)
  if (buf.length > FG_CONSOLE_BUFFER_MAX) buf.splice(0, buf.length - FG_CONSOLE_BUFFER_MAX)
  try {
    window.dispatchEvent(new Event(FG_CONSOLE_EVENT))
  } catch {
    // ignore best-effort console mirror dispatch failures
  }
}

const getNativeDiagnosticConsoleMethod = (
  level: DiagnosticConsoleLevel,
): ((...args: any[]) => void) | null => {
  const w = getWindowAny()
  const nativeConsole = (w?.[FG_NATIVE_CONSOLE_KEY] ?? null) as DiagnosticConsoleMethods | null
  const method = nativeConsole?.[level] ?? nativeConsole?.log ?? null
  return typeof method === 'function' ? method : null
}

export const emitDiagnosticConsoleLine = (
  level: DiagnosticConsoleLevel,
  text: string,
): void => {
  appendDiagnosticConsoleBuffer(level, text)
  if (!shouldMirrorDiagnosticConsoleLine(level)) return
  const nativeMethod = getNativeDiagnosticConsoleMethod(level)
  if (nativeMethod) {
    nativeMethod(text)
    return
  }
  console[level](text)
}

export const emitDiagnosticConsoleJson = (
  prefix: string,
  payload: Record<string, any>,
  level: DiagnosticConsoleLevel = 'log',
): void => {
  try {
    emitDiagnosticConsoleLine(level, `${prefix} ${JSON.stringify(payload)}`)
  } catch {
    emitDiagnosticConsoleLine(level, prefix)
  }
}
